import { create, StoreApi, UseBoundStore } from 'zustand';
import React, { createContext, useContext, useRef, ReactNode } from 'react';

/**
 * Player methods interface - shared between Player and Store
 */
export interface PlayerMethods {
  skipForward: () => void;
  skipBack: () => void;
  setQuality: (height: number) => void;
  getAvailableQualities: () => Array<{ height: number; bandwidth: number; label: string }>;
  seekToLiveEdge: () => void;
  getLiveStatus: () => { isLive: boolean; isNearEdge: boolean; liveEdge?: number };
}

/**
 * Player store state
 */
export interface PlayerStoreState {
  // Video element and player refs
  videoElement: HTMLVideoElement | null;
  shakaPlayer: any | null;
  
  // Playback state
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  isPaused: boolean;
  isEnded: boolean;
  isMuted: boolean;
  volume: number;
  isLoading: boolean;
  
  // Live stream state
  isLive: boolean;
  isNearLiveEdge: boolean;
  liveEdge: number | undefined;
  timeBehindLive: number | undefined;
  
  // Quality state
  currentQuality: { height: number; bandwidth: number } | null;
  availableQualities: Array<{ height: number; bandwidth: number; label: string }>;
  
  // Error state
  error: Error | null;
  
  // Internal methods ref (stored outside Zustand state to avoid triggering re-renders)
  _methodsRef: { current: PlayerMethods | null };
}

/**
 * Player store actions/methods
 */
export interface PlayerStoreActions {
  // Playback controls
  play: () => Promise<void>;
  pause: () => void;
  seek: (timeInSeconds: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  
  // Skip controls
  skipForward: () => void;
  skipBack: () => void;
  
  // Quality controls
  setQuality: (height: number) => void;
  getAvailableQualities: () => Array<{ height: number; bandwidth: number; label: string }>;
  
  // Live controls
  seekToLiveEdge: () => void;
  getLiveStatus: () => { isLive: boolean; isNearEdge: boolean; liveEdge?: number };
  
  // Internal - used by Player component to register/update state
  _registerVideoElement: (video: HTMLVideoElement | null) => void;
  _registerShakaPlayer: (player: any | null) => void;
  _updateState: (updates: Partial<PlayerStoreState>) => void;
  _registerMethods: (methods: PlayerMethods) => void;
}

/**
 * Complete player store (state + actions)
 */
export type PlayerStore = PlayerStoreState & PlayerStoreActions;

/**
 * Factory function to create a player store
 */
function createPlayerStore() {
  return create<PlayerStore>((set, get) => ({
    // Initial state
    videoElement: null,
    shakaPlayer: null,
    currentTime: 0,
    duration: 0,
    isPlaying: false,
    isPaused: true,
    isEnded: false,
    isMuted: false,
    volume: 1,
    isLoading: true,
    isLive: false,
    isNearLiveEdge: false,
    liveEdge: undefined,
    timeBehindLive: undefined,
    currentQuality: null,
    availableQualities: [],
    error: null,
    _methodsRef: { current: null },

    // Playback controls
    play: async () => {
      const { videoElement } = get();
      if (videoElement) {
        try {
          await videoElement.play();
        } catch (error) {
          console.error('Error playing video:', error);
        }
      }
    },

    pause: () => {
      const { videoElement } = get();
      if (videoElement) {
        videoElement.pause();
      }
    },

    seek: (timeInSeconds: number) => {
      const { videoElement } = get();
      if (videoElement) {
        const clampedTime = Math.max(0, Math.min(timeInSeconds, videoElement.duration || Infinity));
        videoElement.currentTime = clampedTime;
      }
    },

    setVolume: (volume: number) => {
      const { videoElement } = get();
      if (videoElement) {
        const clampedVolume = Math.max(0, Math.min(1, volume));
        videoElement.volume = clampedVolume;
      }
    },

    toggleMute: () => {
      const { videoElement } = get();
      if (videoElement) {
        videoElement.muted = !videoElement.muted;
      }
    },

    // Skip controls - delegate to methods registered by Player
    skipForward: () => {
      const { _methodsRef } = get();
      _methodsRef.current?.skipForward();
    },

    skipBack: () => {
      const { _methodsRef } = get();
      _methodsRef.current?.skipBack();
    },

    // Quality controls - delegate to methods registered by Player
    setQuality: (height: number) => {
      const { _methodsRef } = get();
      _methodsRef.current?.setQuality(height);
    },

    getAvailableQualities: () => {
      const { _methodsRef } = get();
      return _methodsRef.current?.getAvailableQualities() || [];
    },

    // Live controls - delegate to methods registered by Player
    seekToLiveEdge: () => {
      const { _methodsRef } = get();
      _methodsRef.current?.seekToLiveEdge();
    },

    getLiveStatus: () => {
      const { _methodsRef } = get();
      return _methodsRef.current?.getLiveStatus() || { isLive: false, isNearEdge: false };
    },

    // Internal methods
    _registerVideoElement: (video: HTMLVideoElement | null) => {
      set({ videoElement: video });
    },

    _registerShakaPlayer: (player: any | null) => {
      set({ shakaPlayer: player });
    },

    _updateState: (updates: Partial<PlayerStoreState>) => {
      set(updates);
    },

    _registerMethods: (methods: PlayerMethods) => {
      // Update the ref without triggering re-renders
      get()._methodsRef.current = methods;
    },
  }));
}

// Type for the store
type PlayerStoreApi = UseBoundStore<StoreApi<PlayerStore>>;

// Context for provider-based stores
const PlayerStoreContext = createContext<PlayerStoreApi | null>(null);

/**
 * PlayerProvider - Wrapper for isolated store instances
 * 
 * Use this when you need multiple player instances on the same page,
 * or when you want to isolate player state from other parts of your app.
 * 
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <PlayerProvider>
 *       <Video videoId="..." publicKey="..." />
 *       <CustomControls />
 *     </PlayerProvider>
 *   );
 * }
 * ```
 */
export function PlayerProvider({ children }: { children: ReactNode }) {
  // Create a store instance for this provider (only once)
  const storeRef = useRef<PlayerStoreApi | null>(null);
  
  if (!storeRef.current) {
    storeRef.current = createPlayerStore();
  }

  return (
    <PlayerStoreContext.Provider value={storeRef.current}>
      {children}
    </PlayerStoreContext.Provider>
  );
}

/**
 * usePlayerStore hook - Access the player store state and methods
 * 
 * When used inside a PlayerProvider, it uses the provider's isolated store.
 * When used outside, it throws an error (store requires a provider).
 * 
 * @example
 * ```tsx
 * function CustomControls() {
 *   const { play, pause, seek, currentTime, duration, isPlaying } = usePlayerStore();
 *   
 *   return (
 *     <div>
 *       <button onClick={play}>Play</button>
 *       <button onClick={pause}>Pause</button>
 *       <button onClick={() => seek(120)}>Jump to 2min</button>
 *       <span>{currentTime} / {duration}</span>
 *     </div>
 *   );
 * }
 * ```
 */
export function usePlayerStore(): PlayerStore {
  const store = useContext(PlayerStoreContext);
  
  if (!store) {
    throw new Error(
      'usePlayerStore must be used within a PlayerProvider. ' +
      'Wrap your component tree with <PlayerProvider> to use the player store.'
    );
  }
  
  return store();
}

/**
 * Hook for Player.tsx to access the store API directly
 * Returns the store instance (not the state) for registering elements
 */
export function usePlayerStoreApi(): PlayerStoreApi | null {
  return useContext(PlayerStoreContext);
}

// Export the context for advanced use cases
export { PlayerStoreContext };
